'use strict';
import React, { useState, useEffect, useCallback } from 'react';
import OpgaveTrad from './OpgaveTrad';

const T = {
  bg:         '#080c1e',
  panel:      '#0d1117',
  sidebar:    '#0a0f1a',
  border:     'rgba(255,255,255,0.07)',
  tekst:      '#e2e8f0',
  sekundaer:  '#64748b',
  hover:      'rgba(255,255,255,0.04)',
  valgt:      'rgba(37,99,235,0.18)',
  valgtBorder:'#2563eb',
  input:      '#111827',
  inputBorder:'1px solid #1e293b',
};

const STATUS_FARVE = {
  KOERER:               '#22c55e',
  AFVENTER:             '#f59e0b',
  AFVENTER_GODKENDELSE: '#a78bfa',
  GODKENDT:             '#3b82f6',
  FULDFOERT:            '#475569',
  FEJLET:               '#ef4444',
  ANNULLERET:           '#374151',
  AFVIST:               '#6b7280',
};

function StatusBadge({ status }) {
  const farve = STATUS_FARVE[status] || '#6b7280';
  return (
    <span style={{
      fontSize: 9, fontWeight: 700, letterSpacing: 0.6,
      color: farve, background: farve + '22',
      border: `1px solid ${farve}55`, borderRadius: 4,
      padding: '2px 6px', textTransform: 'uppercase',
    }}>
      {status?.replace(/_/g, ' ')}
    </span>
  );
}

function fmtTid(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const nu = new Date();
  const diff = (nu - d) / 1000;
  if (diff < 60)   return 'Lige nu';
  if (diff < 3600) return `${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} t`;
  return d.toLocaleDateString('da-DK', { day: '2-digit', month: 'short' });
}

function OpgaveListeItem({ opgave, erValgt, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        width: '100%', textAlign: 'left', cursor: 'pointer',
        background: erValgt ? T.valgt : 'transparent',
        border: 'none',
        borderLeft: erValgt ? `3px solid ${T.valgtBorder}` : '3px solid transparent',
        borderBottom: `1px solid ${T.border}`,
        padding: '12px 14px',
        transition: 'background 0.12s',
      }}
      onMouseEnter={e => { if (!erValgt) e.currentTarget.style.background = T.hover; }}
      onMouseLeave={e => { if (!erValgt) e.currentTarget.style.background = 'transparent'; }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between',
        alignItems: 'flex-start', marginBottom: 5 }}>
        <span style={{ fontSize: 13, fontWeight: erValgt ? 600 : 400,
          color: erValgt ? '#e2e8f0' : '#94a3b8',
          lineHeight: 1.35, flex: 1, marginRight: 8,
          display: '-webkit-box', WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
          {opgave.titel}
        </span>
        <span style={{ fontSize: 10, color: T.sekundaer, flexShrink: 0 }}>
          {fmtTid(opgave.oprettet_kl)}
        </span>
      </div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
        <StatusBadge status={opgave.status} />
        {opgave.afdeling && (
          <span style={{ fontSize: 10, color: T.sekundaer }}>
            {opgave.afdeling}
          </span>
        )}
      </div>
    </button>
  );
}

export default function OSOpgaveChatDashboard({ startOpgaveId }) {
  const [opgaver,      setOpgaver]      = useState([]);
  const [indlæser,     setIndlæser]     = useState(true);
  const [fejl,         setFejl]         = useState(null);
  const [søgning,      setSøgning]      = useState('');
  const [valgtOpgave,  setValgtOpgave]  = useState(null);
  const [side,         setSide]         = useState(0);
  const [nyTitel,      setNyTitel]      = useState('');
  const [nyAfdeling,   setNyAfdeling]   = useState('koordinator');
  const [nyForm,       setNyForm]       = useState(false);
  const [opretter,     setOpretter]     = useState(false);

  const ROUTBARE_AGENTER = [
    'koordinator','overvaagning','kode','deploy','analyse','ai-specialist',
    'oekonomi','marketing','strategisk-raadgiver','gdpr_privacy',
    'kommunikation','social_media','familieret_specialister','klientkoordinering',
  ];

  const SIDE_STR = 100; // antal per load

  const hentOpgaver = useCallback(async (offset = 0, tilfoej = false) => {
    if (offset === 0) setIndlæser(true);
    try {
      const url = `/api/os/opgaver?limit=${SIDE_STR}&offset=${offset}`;
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const rows = Array.isArray(data) ? data : (data.opgaver || []);
      setOpgaver(prev => tilfoej ? [...prev, ...rows] : rows);
      setFejl(null);
    } catch (err) {
      setFejl('Kunne ikke hente opgaver: ' + err.message);
    } finally {
      setIndlæser(false);
    }
  }, []);

  useEffect(() => { hentOpgaver(0, false); }, [hentOpgaver]);

  // Pre-vælg opgave hvis navigeret hertil med startOpgaveId
  useEffect(() => {
    if (startOpgaveId && opgaver.length > 0) {
      const match = opgaver.find(o => o.id === startOpgaveId);
      if (match) setValgtOpgave(match);
    }
  }, [startOpgaveId, opgaver]);

  const filtrerede = søgning.trim()
    ? opgaver.filter(o =>
        o.titel?.toLowerCase().includes(søgning.toLowerCase()) ||
        o.afdeling?.toLowerCase().includes(søgning.toLowerCase()) ||
        o.status?.toLowerCase().includes(søgning.toLowerCase())
      )
    : opgaver;

  const indlæsFlere = () => {
    const nySide = side + 1;
    setSide(nySide);
    hentOpgaver(nySide * SIDE_STR, true);
  };

  const opretOpgave = async () => {
    const titel = nyTitel.trim();
    if (!titel || opretter) return;
    setOpretter(true);
    try {
      const res = await fetch('/api/os/opgaver', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ titel, afdeling: nyAfdeling, beskrivelse: '', prompt: titel }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const ny = await res.json();
      const nyOpgave = { id: ny.id, titel, status: 'AFVENTER', afdeling: nyAfdeling, oprettet_kl: new Date().toISOString() };
      setOpgaver(prev => [nyOpgave, ...prev]);
      setValgtOpgave(nyOpgave);
      setNyTitel('');
      setNyForm(false);
    } catch (err) {
      alert('Kunne ikke oprette opgave: ' + err.message);
    } finally {
      setOpretter(false);
    }
  };

  return (
    <div style={{ display: 'flex', background: T.bg,
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      overflow: 'hidden',
      position: 'fixed', top: 48, left: 0, right: 0, bottom: 0,
    }}>

      {/* ── Venstre: opgaveliste ── */}
      <div style={{ width: 300, minWidth: 260, maxWidth: 360,
        background: T.sidebar, borderRight: `1px solid ${T.border}`,
        display: 'flex', flexDirection: 'column', overflow: 'hidden', flexShrink: 0 }}>

        {/* Header */}
        <div style={{ padding: '12px 14px 10px',
          borderBottom: `1px solid ${T.border}`, flexShrink: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between',
            alignItems: 'center', marginBottom: 8 }}>
            <div style={{ fontSize: 11, color: T.sekundaer, fontWeight: 700,
              textTransform: 'uppercase', letterSpacing: 0.9 }}>
              Alle opgaver
            </div>
            <button onClick={() => setNyForm(v => !v)} style={{
              background: nyForm ? '#1e3a5f' : '#2563eb', border: 'none',
              borderRadius: 6, color: '#fff', fontSize: 12, fontWeight: 600,
              padding: '4px 10px', cursor: 'pointer',
            }}>
              {nyForm ? 'Annuller' : '+ Ny'}
            </button>
          </div>
          {nyForm && (
            <div style={{ marginBottom: 8 }}>
              <input
                type="text"
                autoFocus
                placeholder="Titel på ny chat-opgave…"
                value={nyTitel}
                onChange={e => setNyTitel(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && opretOpgave()}
                style={{ width: '100%', boxSizing: 'border-box',
                  background: T.input, border: '1px solid #2563eb',
                  borderRadius: 7, color: T.tekst, fontSize: 12,
                  padding: '7px 10px', outline: 'none', fontFamily: 'inherit',
                  marginBottom: 6 }}
              />
              <select
                value={nyAfdeling}
                onChange={e => setNyAfdeling(e.target.value)}
                style={{ width: '100%', boxSizing: 'border-box',
                  background: T.input, border: T.inputBorder,
                  borderRadius: 7, color: T.tekst, fontSize: 12,
                  padding: '6px 10px', marginBottom: 6, fontFamily: 'inherit',
                  cursor: 'pointer' }}
              >
                {ROUTBARE_AGENTER.map(a => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </select>
              <button onClick={opretOpgave} disabled={!nyTitel.trim() || opretter}
                style={{ width: '100%', background: nyTitel.trim() ? '#2563eb' : '#1e3a5f',
                  border: 'none', borderRadius: 6, color: nyTitel.trim() ? '#fff' : '#4b5563',
                  fontSize: 12, fontWeight: 600, padding: '6px', cursor: nyTitel.trim() ? 'pointer' : 'not-allowed' }}>
                {opretter ? 'Opretter…' : 'Opret og åbn'}
              </button>
            </div>
          )}
          <input
            type="text"
            placeholder="Søg titel, afdeling, status…"
            value={søgning}
            onChange={e => setSøgning(e.target.value)}
            style={{ width: '100%', boxSizing: 'border-box',
              background: T.input, border: T.inputBorder,
              borderRadius: 7, color: T.tekst, fontSize: 12,
              padding: '7px 10px', outline: 'none', fontFamily: 'inherit' }}
          />
        </div>

        {/* Liste */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {indlæser && (
            <div style={{ color: T.sekundaer, fontSize: 12,
              textAlign: 'center', padding: 24 }}>Indlæser…</div>
          )}
          {fejl && (
            <div style={{ color: '#ef4444', fontSize: 12, padding: 16 }}>{fejl}</div>
          )}
          {!indlæser && filtrerede.length === 0 && (
            <div style={{ color: T.sekundaer, fontSize: 12,
              textAlign: 'center', padding: 24, fontStyle: 'italic' }}>
              Ingen opgaver fundet
            </div>
          )}
          {filtrerede.map(o => (
            <OpgaveListeItem
              key={o.id}
              opgave={o}
              erValgt={valgtOpgave?.id === o.id}
              onClick={() => setValgtOpgave(o)}
            />
          ))}
          {!søgning && opgaver.length >= (side + 1) * SIDE_STR && (
            <button
              onClick={indlæsFlere}
              style={{ width: '100%', background: 'transparent', border: 'none',
                borderTop: `1px solid ${T.border}`, color: T.sekundaer,
                fontSize: 12, padding: '12px', cursor: 'pointer' }}>
              Indlæs flere…
            </button>
          )}
        </div>
      </div>

      {/* ── Højre: tråd ── */}
      <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {valgtOpgave ? (
          <OpgaveTrad
            opgaveId={valgtOpgave.id}
            titel={valgtOpgave.titel}
            opgave={valgtOpgave}
          />
        ) : (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center',
            justifyContent: 'center', flexDirection: 'column', gap: 12 }}>
            <div style={{ fontSize: 40, opacity: 0.15 }}>💬</div>
            <div style={{ color: T.sekundaer, fontSize: 14 }}>
              Vælg en opgave til venstre for at se tråden
            </div>
            <div style={{ color: T.sekundaer, fontSize: 12, opacity: 0.7 }}>
              Skriv til agenten — eller 'udfør' for at starte eksekvering
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
